<template>
  <v-layout>
    <v-flex text-xs-center>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0 pb-1>
            <v-row align="center">
              <div>
                <h1 class="headline">{{ $route.params.supplierType }}</h1>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="filters"
                  label="Find supplier"
                  searchButtonId="findSupplierBtn"
                  ref-name="suppliersListFilters"
                  @searchParamsChanged="searchParamsChanged"
                  :expansionPanel="false"
                ></list-filters>
              </v-col>
              <div>
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                  :ref-name="refNameColumns"
                ></list-visible-columns-and-filter>
              </div>
              <div>
                <v-tooltip
                  v-if="$store.getters.hasAccessTo('suppliers:create')"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      class="ml-2"
                      fab
                      dark
                      color="primary"
                      @click="onCreateNew"
                      id="newSupplierBtn"
                    >
                      <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <span
                    >Create new {{ $route.params.supplierType }} supplier</span
                  >
                </v-tooltip>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <div v-if="showAgGrid" key="ag-grid">
        <ag-grid-vue
          class="ag-theme-alpine"
          id="ag-supp-list"
          :columnDefs="agGridColumns"
          :rowData="items"
          rowSelection="multiple"
          :context="agContext"
          @grid-ready="onAgGridReady"
          @columnMoved="onAgColumnMoved"
          @columnResized="onAgColumnResized"
          @sortChanged="onAgSortChanged"
          @selection-changed="onAgSelectionChanged"
          @newColumnsLoaded="onAgNewColumns"
          suppressDragLeaveHidesColumns
        >
        </ag-grid-vue>
      </div>
      <v-data-table
        v-else
        :headers="filteredHeaders"
        :items="items"
        show-select
        v-model="selected"
        :loading="$apollo.loading"
        v-bind="pagingOptions"
        :search="search"
        item-key="id"
        class="elevation-1"
        fixed-header
        hide-default-footer
        @update:options="onListOptionsUpdate"
        :height="gridHeight"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        ><template v-slot:item.menuActions="{ item }">
          <list-menu-actions
            :item="item"
            @delete="deleteItem"
            @details="viewItem"
            @edit="editItem"
            :title-labeler="(item) => item.name"
            details
            :hide-edit="hideEdit"
            :hide-remove="hideRemove"
          ></list-menu-actions>
        </template>
        <template v-slot:item.notInUse="{ item }">
          <v-simple-checkbox
            v-model="item.notInUse"
            disabled
          ></v-simple-checkbox>
        </template>
        <template v-slot:item.marketPlace="{ item }">
          <v-simple-checkbox
            v-model="item.marketPlace"
            disabled
          ></v-simple-checkbox>
        </template>
        <template v-slot:item.actions="{ item }">
          <default-list-actions
            :item="item"
            :hide-view="!viewOnly"
            :hide-edit="hideEdit"
            :hide-remove="hideRemove"
            @deleteItem="deleteItem($event)"
            @viewItem="viewItem($event)"
            @editItem="editItem($event)"
          >
            <template
              slot="extraButton"
              v-if="!$store.getters.hasAccessTo('suppliers:view_only')"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    class="mx-0"
                    @click="cloneItem(item)"
                  >
                    <v-icon color="primary" small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span>Copy</span>
              </v-tooltip>
              <v-tooltip bottom v-if="showBookNow">
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    class="mx-0"
                    @click="bookItem(item)"
                  >
                    <v-icon color="teal" small>mdi-check</v-icon>
                  </v-btn>
                </template>
                <span>Book Now</span>
              </v-tooltip>
            </template>
          </default-list-actions>
        </template>
        <template v-slot:footer>
          <list-actions
            v-if="selected.length > 0"
            :selected="selected"
            list="suppliers"
            :columns="visibleColumns"
            :filter="searchParams"
            :custom-filter="{
              service_type: $route.params.supplierType.toUpperCase(),
            }"
            @close="selected = []"
            :hide-export="!$store.getters.hasAccessTo('suppliers:export')"
          >
          </list-actions>
          <v-row justify="center" v-if="selected.length > 0">
            <h3 class="py-2 caption">
              {{ selected.length }} record(s) selected
            </h3>
          </v-row>
        </template>
      </v-data-table>

      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="suppliers"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('suppliers')"
      ></get-more-results>
    </v-flex>
    <clone-supplier
      :show="showCloneSupplier"
      v-model="selectedItem"
      @close="showCloneSupplier = false"
      @complete="onCloneComplete"
    ></clone-supplier>
    <v-row v-if="debug">
      <v-col>
        <pre>{{ headers }}</pre>
      </v-col>
    </v-row>
  </v-layout>
</template>

<script>
import { ListMixin } from "../../../mixins/ListMixin";

import {
  ALL_SUPPLIERS_SEARCH_QUERY,
  SUPPLIER_DELETE_MUTATION,
} from "../../../constants/graphql";
import GetMoreResults from "../shared/GetMoreResults";
import DefaultListActions from "../shared/DefaultListActions";
import suppliersFilters from "./suppliers-filters";
import ListFilters from "../shared/list-filters/ListFilters";
import ListVisibleColumnsAndFilter from "../shared/ListVisibleColumnsAndFilter";
import ListMenuActions from "../shared/ListMenuActions";
import ListActions from "../shared/list-actions/ListActions";
import { removeGQLTypename } from "../../../helpers";
import CloneSupplier from "./CloneSupplier";
import AgGridListMixin from "../../../mixins/AgGridListMixin";
import AgGridSwitch from "../shared/AgGridSwitch.vue";
import { AgGridVue } from "ag-grid-vue";

export default {
  name: "supplier-list",
  props: {
    refName: {
      type: String,
      default: () => "supplier-list",
    },
    refNameColumns: {
      type: String,
      default: () => "supplier-list-cols",
    },
  },
  mixins: [ListMixin, AgGridListMixin],
  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
    ListMenuActions,
    CloneSupplier,
    ListActions,
    AgGridSwitch,
    AgGridVue,
  },

  data() {
    return {
      debug: false,
      selected: [],
      suppliers: {},
      searchParams: { not_in_use_eq: false },
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "name",
        "code",
        "destination",
        "actions",
      ]),
      suppliersFilters: suppliersFilters,

      showCloneSupplier: false,
      selectedItem: null,
    };
  },

  methods: {
    cloneItem(item) {
      this.selectedItem = { ...item };
      this.showCloneSupplier = true;
    },
    viewItem(item) {
      this.$router.push("/supplier/" + item.id + "/details");
    },
    bookItem(item) {
      this.$router.push("/supplier/" + item.id + "/book");
    },
    editItem(item) {
      this.$router.push("/supplier/" + item.id + "/details");
    },
    onCreateNew() {
      this.$router.push({
        name: "Supplier Create",
        params: { serviceType: this.$route.params.supplierType },
      });
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: SUPPLIER_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Supplier removed." });
      this.reload();
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },
    searchParamsChanged(event) {
      //console.log("searchParamsChanged", event);
      this.searchParams = { ...event };
    },
    reload() {
      if (this.$apollo.queries.suppliers != null) {
        this.$apollo.queries.suppliers.refetch();
      }
    },
    onCloneComplete(payload) {
      this.$store.dispatch("showAppSnackbar", {
        msg: `${payload.name} Created.`,
      });
      this.reload();
      this.showCloneSupplier = false;
    },
  },

  apollo: {
    suppliers: {
      query: ALL_SUPPLIERS_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          filter: {
            service_type: this.$route.params.supplierType.toUpperCase(),
            ...this.searchParams,
          },
          sort: this.sort,
          first: this.first,
        };
      },
      //fetchPolicy: 'cache-and-network',
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.suppliers.totalCount;
      },
    },
  },

  computed: {
    headers() {
      const list = [
        {
          text: "",
          sortable: false,
          value: "menuActions",
          name: "menuActions",
        },
        {
          text: "Name",
          value: "name",
          name: "name",
        },
        {
          text: "Code",
          value: "code",
          name: "code",
        },
        {
          text: "Basis",
          value: "boardBasis",
          name: "boardBasis",
        },
        {
          text: "Service Type",
          value: "type",
          name: "type",
        },
        {
          text: "Stars Rating",
          value: "rating",
          name: "rating",
        },
        {
          text: "Destination",
          value: "destinationName",
          name: "destination",
          valueGetter: (payload) => payload.data.destinationName,
        },
        {
          text: "Region",
          value: "region",
          name: "region",
        },
        {
          text: "Head Office",
          value: "headOffice.name",
          name: "headOfficeName",
        },
        {
          text: "Address",
          value: "address",
          name: "address",
        },
        {
          text: "Telephone",
          value: "telephone",
          name: "telephone",
        },
        {
          text: "Email",
          value: "email",
          name: "email",
        },
        {
          text: "Accounts Email",
          value: "accountsEmail",
          name: "accountsEmail",
        },
        {
          text: "Not in Use",
          value: "notInUse",
          name: "notInUse",
          align: "center",
        },
        {
          text: "Market Place",
          value: "marketPlace",
          name: "marketPlace",
          align: "center",
        },
        {
          text: "Currency Code",
          value: "currencyCode",
          name: "currencyCode",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
        },
      ];
      return list.filter((h) => {
        if (h.name == "marketPlace") {
          return this.$store.getters.hasAccessTo("third_party:marketplace");
        } else {
          return true;
        }
      });
    },
    items() {
      return this.suppliers && this.suppliers.edges
        ? this.suppliers.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return suppliersFilters
        .map((f) => {
          f.props.outlined =
            this.$store.getters.localSettings("outlinedInputs");
          return f;
        })
        .filter((f) => {
          //console.log(f.props.filterName);
          return f.props.filterName == "market_place_eq"
            ? this.$store.getters.hasAccessTo("third_party:marketplace")
            : true;
        });
    },
    hideEdit() {
      return (
        !this.$store.getters.hasAccessTo("suppliers:edit") ||
        this.$store.getters.hasAccessTo("suppliers:view_only")
      );
    },
    hideRemove() {
      return (
        !this.$store.getters.hasAccessTo("suppliers:delete") ||
        this.$store.getters.hasAccessTo("suppliers:view_only")
      );
    },
    viewOnly() {
      return this.$store.getters.hasAccessTo("suppliers:view_only");
    },

    showBookNow() {
      return this.$store.getters.hasAccessTo("suppliers:book_now");
    },
    // filteredHeaders() {
    //   return [
    //     ...this.headers.filter(
    //       (h) =>
    //         this.visibleColumns.indexOf(h.name) > -1 ||
    //         h.name == "actions" ||
    //         h.name == "menuActions"
    //     ),
    //   ];
    // },
  },
};
</script>

<style scoped>
h1 {
  text-transform: capitalize;
}
.ag-body-viewport-wrapper.ag-layout-normal {
  overflow-x: scroll;
  overflow-y: scroll;
}
#ag-supp-list {
  height: calc(100vh - 220px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/SupplierList.vue